import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  Alert,
  Tooltip,
  CircularProgress,
} from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import DeleteIcon from '@mui/icons-material/Delete'
import AddTaskIcon from '@mui/icons-material/AddTask'
import TranslateIcon from '@mui/icons-material/Translate'
import { useAuth } from '../contexts/AuthContext'
import {
  getRecommendations,
  acceptRecommendation,
  rejectRecommendation,
  deleteRecommendation,
  translateRecommendation,
  Recommendation,
} from '../services/recommendations'
import { getYards, getTeams } from '../services/api'
import { getUsers } from '../services/users'

const TASK_TYPES = [
  'inspection', 'treatment', 'varroaTreatment', 'harvest',
  'movement', 'feeding', 'sugarSyrup', 'pollenSubstitute',
  'requeenHive', 'splitting', 'combReplacement', 'cleanEquipment',
  'winterPrep', 'springPrep', 'queenCheck', 'pesticideAlert',
  'swarmPrevention', 'superAdd', 'superRemove',
  'waxMothTreatment', 'hiveInsulation', 'pollinationService',
  'broodCheck', 'droneRemoval', 'apiarySitePrep',
  'honeyExtraction', 'beePackageInstall', 'medicationCheck',
  'waterSource', 'queenRearing', 'robberBeePrevention',
  'custom',
] as const

const Container = styled.div`
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`

const RecommendationCard = styled(Card)`
  margin-bottom: 16px;
  border-left: 4px solid #f6b400;
  
  &.accepted {
    border-left-color: #10b981;
    opacity: 0.8;
  }
  
  &.rejected {
    border-left-color: #ef4444;
    opacity: 0.6;
  }
`

const RecommendationHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
`

const RecommendationActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 16px;
`

const SourceBadge = styled(Chip)<{ source: string }>`
  background-color: ${props => {
    switch (props.source) {
      case 'voice_assistant':
        return '#E3F2FD'
      case 'system':
        return '#F3E5F5'
      default:
        return '#E0E0E0'
    }
  }};
`

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
`

// Source-language codes we have a translated display name for (recommendations.languages.<code>)
const KNOWN_LANGUAGES = ['en', 'es', 'he', 'ar', 'th']

/**
 * Recommendations screen component
 * @returns Recommendations screen
 */
function RecommendationsScreen(): React.JSX.Element {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { user } = useAuth()

  const viewerLang = (i18n?.language || 'en').split('-')[0]

  const [recommendations, setRecommendations] = useState<Recommendation[]>([])
  const [yards, setYards] = useState<any[]>([])
  const [users, setUsers] = useState<any[]>([])
  const [teams, setTeams] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedTab, setSelectedTab] = useState<'all' | 'pending' | 'accepted' | 'rejected'>('all')

  // Accept dialog state
  const [acceptDialogOpen, setAcceptDialogOpen] = useState(false)
  const [selectedRecommendation, setSelectedRecommendation] = useState<Recommendation | null>(null)
  const [createTask, setCreateTask] = useState(true)
  const [taskType, setTaskType] = useState('')
  const [taskTitle, setTaskTitle] = useState('')
  const [taskDescription, setTaskDescription] = useState('')
  const [taskDueDate, setTaskDueDate] = useState('')
  const [taskYardId, setTaskYardId] = useState('')
  const [assigneeType, setAssigneeType] = useState<'user' | 'team'>('user')
  const [taskAssignee, setTaskAssignee] = useState('')

  // Auto-translation state (keyed by recommendation id)
  const [translated, setTranslated] = useState<Record<string, { title: string; description: string; sourceLanguage: string | null }>>({})
  const [showOriginal, setShowOriginal] = useState<Record<string, boolean>>({})
  const [translating, setTranslating] = useState<Record<string, boolean>>({})

  const doTranslate = useCallback(async (rec: Recommendation) => {
    setTranslating(p => ({ ...p, [rec.id]: true }))
    try {
      const res = await translateRecommendation(rec.id, viewerLang)
      setTranslated(p => ({
        ...p,
        [rec.id]: { title: res.data.title, description: res.data.description, sourceLanguage: res.data.sourceLanguage },
      }))
    } catch (e) {
      console.error('Translate failed', e)
    } finally {
      setTranslating(p => ({ ...p, [rec.id]: false }))
    }
  }, [viewerLang])

  // Auto-translate recommendations whose known source language differs from the viewer's
  useEffect(() => {
    recommendations.forEach(rec => {
      if (rec.sourceLanguage && rec.sourceLanguage !== viewerLang && !translated[rec.id] && !translating[rec.id]) {
        doTranslate(rec)
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recommendations, viewerLang])

  /**
   * Loads all data
   */
  const loadData = useCallback(async () => {
    try {
      setLoading(true)
      const [recsRes, yardsRes, usersRes, teamsRes] = await Promise.all([
        getRecommendations(),
        getYards(),
        getUsers(),
        getTeams(),
      ])

      setRecommendations(recsRes.data || [])
      setYards(yardsRes.data || [])
      setUsers(usersRes.data || [])
      setTeams(teamsRes.data || [])
    } catch (error) {
      console.error('Error loading data:', error)
    } finally {
      setLoading(false)
    }
  }, [])
  
  useEffect(() => {
    loadData()
  }, [loadData])
  
  /**
   * Get yard name by ID
   */
  const getYardName = useCallback((yardId?: string | number): string => {
    if (!yardId) return ''
    const yard = yards.find(y => String(y.id || y._id) === String(yardId))
    return yard?.name || ''
  }, [yards])
  
  /**
   * Get source label
   */
  const getSourceLabel = (source: string): string => {
    switch (source) {
      case 'voice_assistant':
        return t('recommendations.source.voiceAssistant', { defaultValue: 'Voice Assistant' })
      case 'system':
        return t('recommendations.source.system', { defaultValue: 'System' })
      case 'manual':
        return t('recommendations.source.manual', { defaultValue: 'Manual' })
      default:
        return source
    }
  }
  
  /**
   * Handle accept button click
   */
  const handleAcceptClick = (recommendation: Recommendation) => {
    setSelectedRecommendation(recommendation)
    setTaskType(recommendation.recommendedAction || 'custom')
    setTaskTitle(recommendation.title)
    setTaskDescription(recommendation.description)
    setTaskDueDate(new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0])
    setTaskYardId(recommendation.yardId ? String(recommendation.yardId) : '')
    setAssigneeType('user')
    setTaskAssignee(recommendation.userId ? String(recommendation.userId) : '')
    setCreateTask(true)
    setAcceptDialogOpen(true)
  }
  
  /**
   * Handle accept confirmation
   */
  const handleAcceptConfirm = async () => {
    if (!selectedRecommendation) return
    
    try {
      await acceptRecommendation(selectedRecommendation.id, {
        createTask,
        taskData: createTask ? {
          title: taskTitle,
          description: taskDescription,
          dueDate: taskDueDate ? new Date(taskDueDate).toISOString() : new Date().toISOString(),
          type: taskType || undefined,
          yardId: taskYardId || undefined,
          userId: assigneeType === 'user' ? (taskAssignee || undefined) : undefined,
          teamId: assigneeType === 'team' ? (taskAssignee || undefined) : undefined,
        } : undefined,
      })
      
      setAcceptDialogOpen(false)
      setSelectedRecommendation(null)
      loadData()
    } catch (error) {
      console.error('Error accepting recommendation:', error)
    }
  }
  
  /**
   * Handle reject
   */
  const handleReject = async (id: string) => {
    if (!window.confirm(t('recommendations.confirmReject', { defaultValue: 'Are you sure you want to reject this recommendation?' }))) {
      return
    }
    
    try {
      await rejectRecommendation(id)
      loadData()
    } catch (error) {
      console.error('Error rejecting recommendation:', error)
    }
  }
  
  /**
   * Handle delete
   */
  const handleDelete = async (id: string) => {
    if (!window.confirm(t('recommendations.confirmDelete', { defaultValue: 'Are you sure you want to delete this recommendation?' }))) {
      return
    }
    
    try {
      await deleteRecommendation(id)
      loadData()
    } catch (error) {
      console.error('Error deleting recommendation:', error)
    }
  }
  
  /**
   * Filter recommendations by status
   */
  const filteredRecommendations = recommendations.filter(rec => {
    if (selectedTab === 'all') return true
    return rec.status === selectedTab
  })
  
  if (loading) {
    return (
      <Container>
        <LoadingSpinner>
          <Typography>{t('common.loading', { defaultValue: 'Loading...' })}</Typography>
        </LoadingSpinner>
      </Container>
    )
  }
  
  return (
    <Container>
      <Header>
        <Typography variant="h4" fontWeight={700}>
          {t('recommendations.title', { defaultValue: 'Recommendations' })}
        </Typography>
        <Button variant="outlined" onClick={() => navigate('/dashboard')}>
          {t('button.back.text', { defaultValue: 'Back' })}
        </Button>
      </Header>
      
      <Box mb={3}>
        <Tabs value={selectedTab} onChange={(_, value) => setSelectedTab(value)} sx={{ '& .MuiTab-root': { textTransform: 'none' } }}>
          <Tab
            value="all"
            label={`${t('recommendations.all', { defaultValue: 'All' })} (${recommendations.length})`}
          />
          <Tab
            value="pending"
            label={`${t('recommendations.pending', { defaultValue: 'Pending' })} (${recommendations.filter(r => r.status === 'pending').length})`}
          />
          <Tab
            value="accepted"
            label={`${t('recommendations.accepted', { defaultValue: 'Accepted' })} (${recommendations.filter(r => r.status === 'accepted').length})`}
          />
          <Tab
            value="rejected"
            label={`${t('recommendations.rejected', { defaultValue: 'Rejected' })} (${recommendations.filter(r => r.status === 'rejected').length})`}
          />
        </Tabs>
      </Box>
      
      {filteredRecommendations.length === 0 ? (
        <Card>
          <CardContent>
            <Typography variant="body1" color="text.secondary">
              {t('recommendations.noRecommendations', { defaultValue: 'No recommendations found' })}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        filteredRecommendations.map(recommendation => {
          const tr = translated[recommendation.id]
          const isOriginal = !!showOriginal[recommendation.id]
          const showTranslated = !!tr && !isOriginal
          const displayTitle = showTranslated ? tr.title : recommendation.title
          const displayDescription = showTranslated ? tr.description : recommendation.description
          const srcLang = tr?.sourceLanguage || recommendation.sourceLanguage || null
          const srcLabel = srcLang
            ? (KNOWN_LANGUAGES.includes(srcLang) ? t(`recommendations.languages.${srcLang}`, { defaultValue: srcLang }) : srcLang)
            : null
          const isBusy = !!translating[recommendation.id]
          // Offer manual translate only when source is unknown or known-different and not yet translated
          const canManualTranslate = !tr && !isBusy && (!recommendation.sourceLanguage || recommendation.sourceLanguage !== viewerLang)
          return (
          <RecommendationCard key={recommendation.id} className={recommendation.status}>
            <CardContent>
              <RecommendationHeader>
                <Box>
                  <Typography variant="h6" fontWeight={600}>
                    {displayTitle}
                  </Typography>
                  <Box display="flex" gap={1} mt={1}>
                    <SourceBadge
                      label={getSourceLabel(recommendation.source)}
                      size="small"
                      source={recommendation.source}
                    />
                    {recommendation.recommendedAction && (
                      <Chip
                        label={t(`tasks.types.${recommendation.recommendedAction}`, { defaultValue: recommendation.recommendedAction })}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    )}
                  </Box>
                </Box>
                <Chip
                  label={t(`recommendations.status.${recommendation.status}`, { defaultValue: recommendation.status })}
                  color={
                    recommendation.status === 'accepted'
                      ? 'success'
                      : recommendation.status === 'rejected'
                      ? 'error'
                      : 'warning'
                  }
                />
              </RecommendationHeader>
              
              <Typography variant="body1" paragraph>
                {displayDescription}
              </Typography>

              {/* Translation indicator / controls */}
              {(showTranslated || isBusy || canManualTranslate) && (
                <Box display="flex" alignItems="center" gap={1} mb={1} flexWrap="wrap">
                  {isBusy ? (
                    <Box display="flex" alignItems="center" gap={0.5} sx={{ color: 'text.secondary', fontSize: 13 }}>
                      <CircularProgress size={14} />
                      {t('recommendations.translating', { defaultValue: 'Translating…' })}
                    </Box>
                  ) : showTranslated ? (
                    <>
                      <Tooltip title={t('recommendations.translatedTooltip', {
                        defaultValue: 'Auto-translated to your language',
                      })}>
                        <Chip
                          icon={<TranslateIcon />}
                          size="small"
                          color="info"
                          variant="outlined"
                          label={
                            srcLabel
                              ? t('recommendations.translatedFrom', { lang: srcLabel, user: recommendation.userName })
                              : t('recommendations.translated')
                          }
                        />
                      </Tooltip>
                      <Button size="small" onClick={() => setShowOriginal(p => ({ ...p, [recommendation.id]: true }))}>
                        {t('recommendations.showOriginal', { defaultValue: 'Show original' })}
                      </Button>
                    </>
                  ) : isOriginal && tr ? (
                    <Button size="small" startIcon={<TranslateIcon />} onClick={() => setShowOriginal(p => ({ ...p, [recommendation.id]: false }))}>
                      {t('recommendations.showTranslation', { defaultValue: 'Show translation' })}
                    </Button>
                  ) : canManualTranslate ? (
                    <Button size="small" startIcon={<TranslateIcon />} onClick={() => doTranslate(recommendation)}>
                      {t('recommendations.translate', { defaultValue: 'Translate' })}
                    </Button>
                  ) : null}
                </Box>
              )}

              <Typography variant="caption" color="text.secondary" display="block">
                {recommendation.yardName && `${recommendation.yardName} • `}
                {new Date(recommendation.createdAt).toLocaleDateString(i18n?.language)} •{' '}
                {t('recommendations.reportedBy', { user: recommendation.userName, defaultValue: 'Reported by {{user}}' })}
              </Typography>
              
              {recommendation.status === 'pending' && (
                <RecommendationActions>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<CheckCircleIcon />}
                    onClick={() => handleAcceptClick(recommendation)}
                  >
                    {t('recommendations.accept', { defaultValue: 'Accept' })}
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<CancelIcon />}
                    onClick={() => handleReject(recommendation.id)}
                  >
                    {t('recommendations.reject', { defaultValue: 'Reject' })}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<DeleteIcon />}
                    onClick={() => handleDelete(recommendation.id)}
                  >
                    {t('button.delete.text', { defaultValue: 'Delete' })}
                  </Button>
                </RecommendationActions>
              )}
              
              {recommendation.status === 'accepted' && recommendation.taskId && (
                <Box mt={2}>
                  <Typography variant="caption" color="success.main">
                    ✓ {t('recommendations.taskCreated', { defaultValue: 'Task created' })}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </RecommendationCard>
          )
        })
      )}
      
      {/* Accept Dialog */}
      <Dialog open={acceptDialogOpen} onClose={() => setAcceptDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {t('recommendations.acceptTitle', { defaultValue: 'Accept Recommendation' })}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={2}>
            <FormControl fullWidth>
              <InputLabel>{t('recommendations.createTask', { defaultValue: 'Create Task' })}</InputLabel>
              <Select
                value={createTask ? 'yes' : 'no'}
                onChange={(e) => setCreateTask(e.target.value === 'yes')}
                label={t('recommendations.createTask', { defaultValue: 'Create Task' })}
              >
                <MenuItem value="yes">
                  {t('recommendations.yesCreateTask', { defaultValue: 'Yes, create a task' })}
                </MenuItem>
                <MenuItem value="no">
                  {t('recommendations.noCreateTask', { defaultValue: 'No, just accept' })}
                </MenuItem>
              </Select>
            </FormControl>
            
            {createTask && (
              <>
                <FormControl fullWidth>
                  <InputLabel>
                    {t('recommendations.taskType', { defaultValue: 'Task Type' })}
                  </InputLabel>
                  <Select
                    value={taskType}
                    onChange={(e) => setTaskType(e.target.value)}
                    label={t('recommendations.taskType', { defaultValue: 'Task Type' })}
                  >
                    {TASK_TYPES.map(type => (
                      <MenuItem key={type} value={type}>
                        {t(`tasks.types.${type}`, {
                          defaultValue: type.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim(),
                        })}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {taskType === 'custom' && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    {t('recommendations.customTypeHint', { defaultValue: 'This task uses a custom type. You can enter a specific type name in the title or description.' })}
                  </Alert>
                )}
                <TextField
                  label={t('recommendations.taskTitle', { defaultValue: 'Task Title' })}
                  value={taskTitle}
                  onChange={(e) => setTaskTitle(e.target.value)}
                  fullWidth
                  required
                />
                <TextField
                  label={t('recommendations.taskDescription', { defaultValue: 'Task Description' })}
                  value={taskDescription}
                  onChange={(e) => setTaskDescription(e.target.value)}
                  fullWidth
                  multiline
                  rows={3}
                />
                <TextField
                  label={t('recommendations.taskDueDate', { defaultValue: 'Due Date' })}
                  type="date"
                  value={taskDueDate}
                  onChange={(e) => setTaskDueDate(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
                <FormControl fullWidth>
                  <InputLabel>
                    {t('recommendations.yard', { defaultValue: 'Yard' })}
                  </InputLabel>
                  <Select
                    value={taskYardId}
                    onChange={(e) => setTaskYardId(e.target.value)}
                    label={t('recommendations.yard', { defaultValue: 'Yard' })}
                  >
                    <MenuItem value="">
                      {t('recommendations.none', { defaultValue: 'None' })}
                    </MenuItem>
                    {yards.map(y => (
                      <MenuItem key={y.id || y._id} value={String(y.id || y._id)}>
                        {y.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel>
                    {t('recommendations.assigneeType', { defaultValue: 'Assignee Type' })}
                  </InputLabel>
                  <Select
                    value={assigneeType}
                    onChange={(e) => {
                      setAssigneeType(e.target.value as 'user' | 'team')
                      setTaskAssignee('')
                    }}
                    label={t('recommendations.assigneeType', { defaultValue: 'Assignee Type' })}
                  >
                    <MenuItem value="user">
                      {t('recommendations.user', { defaultValue: 'User' })}
                    </MenuItem>
                    <MenuItem value="team">
                      {t('recommendations.team', { defaultValue: 'Team' })}
                    </MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel>
                    {t('recommendations.assignTo', { defaultValue: 'Assign To' })}
                  </InputLabel>
                  <Select
                    value={taskAssignee}
                    onChange={(e) => setTaskAssignee(e.target.value)}
                    label={t('recommendations.assignTo', { defaultValue: 'Assign To' })}
                  >
                    <MenuItem value="">
                      {t('recommendations.none', { defaultValue: 'None' })}
                    </MenuItem>
                    {assigneeType === 'user'
                      ? users.map(u => (
                          <MenuItem key={u.id} value={u.id}>
                            {u.name || u.email}
                          </MenuItem>
                        ))
                      : teams.map(team => (
                          <MenuItem key={team.id || team._id} value={String(team.id || team._id)}>
                            {team.name}
                          </MenuItem>
                        ))
                    }
                  </Select>
                </FormControl>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAcceptDialogOpen(false)}>
            {t('button.cancel.text', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleAcceptConfirm}
            startIcon={createTask ? <AddTaskIcon /> : <CheckCircleIcon />}
          >
            {createTask
              ? t('recommendations.acceptAndCreateTask', { defaultValue: 'Accept & Create Task' })
              : t('recommendations.acceptOnly', { defaultValue: 'Accept' })}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  )
}

export default RecommendationsScreen
